"use client"
import { useState, useRef, useCallback } from "react"
import { Upload, X, ImageIcon } from "lucide-react"
import { Input } from "@/components/ui/input"

interface Props {
  imageUrl: string
  imageSourceUrl: string
  imageSourceName: string
  onImageChange: (url: string) => void
  onSourceUrlChange: (url: string) => void
  onSourceNameChange: (name: string) => void
  /** Label for source name field — e.g. "Image credit" for articles */
  sourceNameLabel?: string
  /** Show the licensing agreement checkbox (for public-facing forms) */
  requireLicenseAgreement?: boolean
}

export function ImageUpload({
  imageUrl,
  imageSourceUrl,
  imageSourceName,
  onImageChange,
  onSourceUrlChange,
  onSourceNameChange,
  sourceNameLabel = "Image source name",
  requireLicenseAgreement = false,
}: Props) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState("")
  const [dragOver, setDragOver] = useState(false)
  const [licenseAgreed, setLicenseAgreed] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const uploadFile = useCallback(async (file: File) => {
    setError("")

    // Validate client-side
    const allowed = ["image/jpeg", "image/png", "image/webp"]
    if (!allowed.includes(file.type)) {
      setError("Only JPG, PNG, and WebP images are accepted.")
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be under 5 MB.")
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append("file", file)
      const res = await fetch("/api/upload", { method: "POST", body: formData })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error ?? "Upload failed")
      }
      const { url } = await res.json()
      onImageChange(url)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed")
    } finally {
      setUploading(false)
    }
  }, [onImageChange])

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) uploadFile(file)
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) uploadFile(file)
  }

  function removeImage() {
    onImageChange("")
    onSourceUrlChange("")
    onSourceNameChange("")
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium">Cover Image</label>

      {imageUrl ? (
        /* Preview */
        <div className="relative rounded-xl overflow-hidden border border-[var(--border)]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageUrl}
            alt="Cover preview"
            className="w-full h-40 object-cover"
          />
          <button
            type="button"
            onClick={removeImage}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
            title="Remove image"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        /* Drop zone */
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
            dragOver
              ? "border-[var(--primary)] bg-[var(--primary)]/5"
              : "border-[var(--border)] hover:border-[var(--primary)]/50"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-2 text-[var(--muted-foreground)]">
              <div className="w-5 h-5 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm">Uploading...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-[var(--muted-foreground)]">
              <Upload size={24} />
              <span className="text-sm font-medium">
                Drop an image here or click to browse
              </span>
              <span className="text-xs">
                JPG, PNG, or WebP &middot; Max 5 MB
              </span>
            </div>
          )}
        </div>
      )}

      {/* URL input as alternative */}
      <div>
        <label className="block text-xs text-[var(--muted-foreground)] mb-1">
          Or paste an image URL
        </label>
        <Input
          type="url"
          value={imageUrl}
          onChange={(e) => onImageChange(e.target.value)}
          placeholder="https://..."
        />
      </div>

      {/* Source / attribution fields */}
      {imageUrl && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1">Image source URL</label>
            <Input
              type="url"
              value={imageSourceUrl}
              onChange={(e) => onSourceUrlChange(e.target.value)}
              placeholder="https://... (where the image came from)"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">{sourceNameLabel}</label>
            <Input
              value={imageSourceName}
              onChange={(e) => onSourceNameChange(e.target.value)}
              placeholder="e.g. Unsplash / Jane Doe"
            />
          </div>
        </div>
      )}

      {/* Licensing agreement */}
      {requireLicenseAgreement && imageUrl && (
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={licenseAgreed}
            onChange={(e) => setLicenseAgreed(e.target.checked)}
            className="rounded mt-0.5"
            required
          />
          <span className="text-xs text-[var(--muted-foreground)] leading-relaxed">
            I confirm that I have the right to use this image. It is either my own photo,
            used with the photographer&apos;s permission, or sourced from a site with a
            compatible license (e.g. Unsplash, Creative Commons).
          </span>
        </label>
      )}

      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <ImageIcon size={12} /> {error}
        </p>
      )}
    </div>
  )
}
